import React, { act, useEffect, useState } from "react";
import Card from "../ui/Card";
import VirtualTable from "../ui/VirtualTable";
import { pubCall } from "../utils/PubSub";
import { showError } from "../ui/Message";
import TextButton from "../ui/TextButton";
import Space from "../ui/Space";
import Label from "../ui/Label";
import Status from "../ui/Status";
import { InfoIcon } from "../utils/Icons";
import Input from "../ui/Input";
import Checkbox from "../ui/Checkbox";
import Float from "../ui/Float";
import { listen } from "../hooks/Listener";
import { KBD } from "../utils/Keybindings";
import { centsToPrice, drawPrice, kbdEvent, moveItem, showConfirm, showInfo, sumByKey } from "../utils/Utils";
import { useTabs } from "../hooks/useTabs";
import PriceField from "../ui/PriceField";
import { ROUTES } from "../utils/Routes";
import Divider from "../ui/Divider";
import Button from "../ui/Button";
import PrintOptions from "./PrintOptions";
import { STUB_PRINTER } from "../utils/Struct";
import Spin from "../ui/Spin";
import { pos_capturePayment } from "../controllers/POSController";
import { createModal } from "../hooks/createModal";
import PaymentFailed from "../popups/PaymentFailed";


export const pos_parsePrintOptions = (print_options = [], printer_settings = {}) => {
    return print_options.map(item => {
        let _stub = { ...STUB_PRINTER };

        switch (item.destination) {
            case "M":
                _stub = printer_settings?.printer_main_printer;
                break;
            case "R":
                _stub = printer_settings?.printer_receipt_printer;
                break;
            case "I":
                _stub = printer_settings?.printer_invoice_printer;
                break;
            default:
                _stub.printer_name = item.printer_email_file;
        }

        return {
            copies: parseInt(item.copies_options, 10) || 1,
            document_type: item.format,
            printer_type: item.destination,
            printer: _stub,
        };
    });
};

export default function POSTender(props) {
    const { controller = "", route = "", callback = () => { }, invoice = "", header = {}, employee = "" } = props;



    const [results, setResults] = useState({
        amount_owing: 0,
        calculated_totals: {
            discounts: 0,
            item_total: 0,
            sub_total: 0,
            tax_total: 0,
            taxes: [],
            total: 0
        },
        tenders: []
    });

    const [products, setProducts] = useState([])
    const [productsTotal, setProductsTotal] = useState("")
    const [loading, setLoading] = useState(false);
    const [captureAmount, setCaptureAmount] = useState(null); // set while a pinpad payment is in progress

    const [state, setState] = useState({
        view: "tender",
        prefill: 700
    })



    const [printerRows, setPrinterRows] = useState([
        {
            id: "default",
            format: "R",
            destination: "R",
            copies_options: "1",
            printer_email_file: ""
        }
    ]);



    const row_key = "to_store_name";

    const FX_RATE = 1.25;
    const DISCOUNT_TYPES = [1, 2, 6];
    const FOREIGN_CURRENCY_TYPE = 25;
    const EXCHANGE_TYPE = 26;
    const CASH_TYPE = 23;



    const getDefaultTender = (header = {}, tenders = []) => {
        console.log("Here", header, tenders, window.pos_settings?.default_tender_customer, window.pos_settings?.default_tender)
        const hasCustomer = (header?.customer_ship_to?.ship_to);
        const defaultTender = (hasCustomer) ? window.pos_settings?.default_tender_customer : window.pos_settings?.default_tender;
        return tenders.find(item => item.to_store_name === defaultTender?.name);
    };

    const switchToTenders = () => {
        blurField();
        window.bm_state.value = route;

        setState(prev => ({
            ...prev,
            view: "tender",
        }));
        focusField();
    };

    const switchToPrint = () => {
        blurField();
        window.bm_state.value = ROUTES.POS.PRINT;
        setState(prev => ({
            ...prev,
            view: "print",
        }));
    };

    const switchToPrintEdit = () => {
        window.bm_state.value = ROUTES.POS.PRINT_EDIT;
        setState(prev => ({
            ...prev,
            view: "print_edit",
        }));
    };


    const isPrintView = state.view === "print" || state.view === "print_edit";
    const isPrintEdit = state.view === "print_edit";

    const fetchItems = () => {
        if (!invoice) { return; }
        // getInitialItem returns the last rows for point of sale - newest rows (e.g. payments just captured) are at the end
        pubCall("point_of_sale.getInitialItem", { sort: "id", invoice: header?.invoice, limit: 40 }, (_s, _r) => {
            if (_s) {
                setProducts((_r?.rows_previous || []).concat(_r?.rows_next || []));
                setProductsTotal(_r?.total);
            }
        })
    }

    useEffect(fetchItems, []);



    const isDiscountTender = (tender) =>
        DISCOUNT_TYPES.includes(Number(tender?.type));

    const isForeignCurrencyTender = (tender) =>
        Number(tender?.type) === FOREIGN_CURRENCY_TYPE;

    const isExchangeTender = (tender) =>
        Number(tender?.type) === EXCHANGE_TYPE;

    const isCashTender = (tender) =>
        Number(tender?.type) === CASH_TYPE;


    const isSelectableTender = (tender) =>
        !isExchangeTender(tender);


    const getForeignCurrencyValues = (amount) => {
        const roundedTotal = amount + getCashAdjustment(amount);
        const foreignAmount = Math.round(roundedTotal / FX_RATE);
        const exchangeAmount = roundedTotal - foreignAmount;

        return {
            foreignAmount,
            exchangeAmount,
            label: `$${drawPrice(exchangeAmount, true)} @${FX_RATE}`
        };
    };



    const [selected, setSelected] = useState({})
    const [notesOpen, setNotesOpen] = useState({});
    const [align, setAlign] = useState("auto");



    // 

    const capturingPayment = React.useRef(false);

    const isPinPadTender = tender =>
        String(tender?.to_store_name || "").trim().toLowerCase() === "pinpad";

    const capturePayment = (amount) => {
        if (capturingPayment.current || loading) return;

        if (!invoice) {
            showError({ error: "A saved invoice is required to capture payment." });
            return;
        }

        const paymentAmount = Number(amount);

        if (!Number.isSafeInteger(paymentAmount) || paymentAmount <= 0) {
            showError({ error: "Enter a valid payment amount." });
            return;
        }

        capturingPayment.current = true;

        const returnRoute = window.bm_state.value;
        const owing = Number(results?.amount_owing) || 0;
        const remaining = owing - paymentAmount;
        const isPartial = remaining > 0;

        const showPaymentResult = (title, body, type = "error", colors = {}) => {
            showInfo({
                controller,
                route: returnRoute,
                type,
                ...colors,
                title,
                description: <div style={{ "textAlign": "center", "maxWidth": "340px", "lineHeight": "18px" }}>{body}</div>,
                callback: () => { },
            });
        };

        showConfirm({
            controller: controller,
            route: returnRoute,
            title: isPartial ? "Partial Payment" : "Pinpad Payment",
            description: (
                <div style={{ "textAlign": "center" }}>
                    {isPartial
                        ? <>${drawPrice(paymentAmount, true)} entered for Pinpad is not the ${drawPrice(owing, true)} required.<div className="shim" />Charge ${drawPrice(paymentAmount, true)} to this card and pay the remaining ${drawPrice(remaining, true)} with another tender?</>
                        : <>Charge ${drawPrice(paymentAmount, true)} to Pinpad?</>
                    }
                </div>
            ),
            callback: (e) => {
                if (!e) {
                    capturingPayment.current = false;
                    return;
                }

                // Block the screen (mouse + keys) while waiting on the payment service
                setCaptureAmount(paymentAmount);
                window.bm_state.value = ROUTES.POS.PAYMENT_PROCESSING;
                pos_capturePayment( { invoice, amount: paymentAmount, clerk_code: employee }, (_s, _r) => {
                        capturingPayment.current = false;
                        setCaptureAmount(null);
                        window.bm_state.value = returnRoute;

                        if (!_s || !_r?.approved) {
                            const message = _r?.message || _r?.error || "Payment was not approved.";
                            const declined = (_s && _r?.approved === false) ? <><strong>Card Transaction Not Completed</strong><div className="shim" /></> : null;
                            const code = (_r?.result_code) ? <><div className="shim" />Result code {_r.result_code}</> : null;
                            const unknown = (_r?.outcome_unknown) ? <><div className="shim" /><strong>The payment outcome is unknown. Verify on the pinpad before retrying.</strong></> : null;

                            window.bm_state.value = ROUTES.HELPERS.INFO;
                            createModal({
                                alert: true,
                                closable: true,
                                title: "Payment Failed",
                                component: <PaymentFailed
                                    controller={controller}
                                    route={ROUTES.HELPERS.INFO}
                                    title="Payment Failed"
                                    description={<div style={{ "textAlign": "center", "maxWidth": "340px", "lineHeight": "18px" }}>{declined}{message}{code}{unknown}</div>}
                                />,
                                callback: () => {
                                    window.bm_state.value = returnRoute;
                                    // Refresh after dismiss - refreshing while open re-renders the tender inputs and steals focus from [OK]
                                    getTenderOptions();
                                    fetchItems();
                                }
                            });
                            return;
                        }

                        getTenderOptions();
                        fetchItems();

                        showPaymentResult(
                            "Payment Approved",
                            <>
                                ${drawPrice(paymentAmount, true)} was approved.
                                {(remaining > 0) && <><div className="shim" />You still need a payment of ${drawPrice(remaining, true)}.</>}
                            </>,
                            "info",
                            { color: "#000", textColor: "#fff" }
                        );
                    }
                );
            }
        });
    };


    // useEffect(() => {

    //         const cleanup = listen(controller, ROUTES.POS.POS, pos_nav);
    //         return cleanup;
    //     }, [pos_nav])


    const getCashNote = (_str) => {
        if (!_str) return "";
        const index = _str.indexOf("$");
        return index === -1 ? "" : _str.slice(index);
    };


    const getCashAdjustment = (amount = 0) => {
        const remainder = amount % 5;

        switch (remainder) {
            case 1: return -1;
            case 2: return -2;
            case 3: return 2;
            case 4: return 1;
            default: return 0;
        }
    };

    const applyCashRounding = (tender) => {
        if (tender?.to_store_name !== "Cash") return tender;

        const adjustment = getCashAdjustment(tender.amount || 0);

        return {
            ...tender,
            penny_adjustment: adjustment,
            rounded_amount: (tender.amount || 0) + adjustment
        };
    };


    const blurField = (_field = index) => {
        const el = refs.current[_field];
        if (el && typeof el.blur === 'function') {
            el.blur();
        }
    }

    const focusField = (_field = index) => {
        const el = refs.current[_field];
        if (el && typeof el.focus === 'function') {
            el.focus();
        }
    }

    const getSelectableTenders = () => (tabs || results?.tenders || []).filter(isSelectableTender);

    const nextRow = (_rows = 1) => {
        moveItem({ data: getSelectableTenders(), selected, setSelected, row_key: row_key, step: _rows, setAlign, alignOnMove: false })
    };
    const prevRow = (_rows = 1) => {
        moveItem({ data: getSelectableTenders(), selected, setSelected, row_key: row_key, step: -_rows, setAlign, alignOnMove: true })
    };



    const finalizeOrder = (_print_options, _printer_settings) => {
        finalizeOrderWithoutReceipt(selected, _print_options, _printer_settings);
    }

    const finalizeOrderWithoutReceipt = (_row = selected, print_options = false, _printer_settings = {}) => {


        _row.amount = state.prefill;
        const update = tabs.map(tab =>
            tab.to_store_name === _row.to_store_name
                ? { ...tab, ..._row }
                : tab
        ).filter(item => item.amount);

        let _api = "point_of_sale.finalizeTransaction";

        let _obj = {
            is_offsite: false,
            tenders_in: update,
            use_frequent_buyer_points: false,
            invoice: invoice,
            clerk_code: employee
        }

        if (print_options) {
            _obj.options = {
                sort: "I",
                print_options: pos_parsePrintOptions(print_options, _printer_settings)
            };
        }








        // Tender Only
        if (!invoice) {
            if (update?.length < 2) {
                showError({ error: "Must have at least two tendors" })
                return;
            }

            _api = "point_of_sale.finalizeTenderOnlyTransaction";
            _obj = {
                tenders_in: update,
                header: header,
                clerk_code: employee
            }
        }

        setLoading(true);
        pubCall(_api, _obj, (_s, _r) => {
            if (_s) {
                callback(_r);
            } else {
                showError(_r);
            }
            setLoading(false)
        })
    }

    const tender_actions = [
        { key: KBD.ESCAPE, action: () => callback() },
        { key: KBD.MINUS, action: () => toggleRef(selected) },
        { key: KBD.ARROW_DOWN, action: () => { commitSelectedPrice(1); } },
        { key: KBD.ARROW_UP, action: () => { commitSelectedPrice(-1); } },
        {
            key: KBD.PAGE_DOWN,
            action: () => {
                if (isPinPadTender(selected)) {
                    const amount = getVal(selected.to_store_name);
                    capturePayment(amount === "" || amount == null || Number(amount) === 0 ? state.prefill : amount);
                    return;
                }

                switchToPrintEdit();
            },
        },
        {
            key: KBD.PAGE_UP, action: () => {
                finalizeOrderWithoutReceipt();
            }
        }



    ];

    const final_actions = [
        // { key: KBD.ESCAPE, action: () => setState({ ...state, "view": "tender" }) },
        // { key: KBD.ARROW_DOWN, action: () => { nextRow() } },
        // { key: KBD.ARROW_UP, action: () => prevRow() },
        // { key: KBD.PAGE_DOWN, action: () => finalizeOrder() }

    ];

    // useEffect(() => {
    //     if (state.view === "tender") {
    //         window.bm_state.value = route;
    //     }

    //     if (state.view === "print") {
    //         window.bm_state.value = ROUTES.POS.PRINT;
    //     }

    //     if (state.view === "print_edit") {
    //         window.bm_state.value = ROUTES.POS.PRINT_EDIT;
    //     }
    // }, [state.view, route]);



    // <div style="width: clamp(1180px, 100vw, 1440px); height: calc(-50px + 100vh); min-height: 598px; margin: 0px auto;">


    // </div>



    const setTenderOptions = (amount, tender = {}, goto_cash = false, move = 0) => {

        // update tender
        let updatedTender = {
            ...tender,
            amount: parseInt(amount || 0)
        };

        const update = tabs.map(tab =>
            tab.to_store_name === updatedTender.to_store_name
                ? { ...tab, ...updatedTender }
                : tab
        ).filter(item => item.amount);


        let _obj = {
            invoice: invoice,
            use_frequent_buyer_points: false,
            tenders_in: update,
        }

        let _api = "point_of_sale.getTransactionTenderOptions"

        if (!invoice) {
            _api = "point_of_sale.getTenderOnlyTenderOptions";
            _obj = {
                header: header,
                tenders_in: update,
            }
        }



        pubCall(
            "point_of_sale.getTransactionTenderOptions",
            {
                invoice,
                use_frequent_buyer_points: false,
                tenders_in: update
            },
            (_s, _r) => {
                if (_s) {
                    setResults(_r);
                    setState(prev => ({
                        ...prev,
                        prefill: _r.amount_owing
                    }));

                    // Cash is highter, go to change..
                    if (isCashTender(updatedTender) && updatedTender.amount > results.amount_owing) {
                        const change = _r.tenders.find(
                            item => item.to_store_name === "Change"
                        );

                        if (change) { setSelected(change); }

                        return;
                    }

                    if (goto_cash) {
                        let _cash = _r.tenders.findIndex(item => item.to_store_name === "Cash");
                        if (_cash > -1) {
                            setSelected(_r?.tenders[_cash]);
                        }
                        return;
                    }

                    if (move === 1) {
                        nextRow()
                    }
                    if (move === -1) {
                        prevRow();
                    }


                } else {
                    showError(_r);
                }
            }
        );
    };

    const getTenderOptions = () => {


        let _obj = {
            invoice: invoice,
            use_frequent_buyer_points: false,
        }

        let _api = "point_of_sale.getTransactionTenderOptions"

        if (!invoice) {
            _api = "point_of_sale.getTenderOnlyTenderOptions";
            _obj = {
                header: header,
                tenders_in: [],
            }
        }

        pubCall(_api, _obj, (_s, _r) => {
            if (_s) {
                setResults(_r);
                setState({ ...state, "prefill": _r.amount_owing })

                const tender = getDefaultTender(header, _r.tenders);
                if (tender) {
                    setSelected(tender);
                }
            } else {
                showError(_r);
            }
        })
    }

    useEffect(getTenderOptions, [])



    const DrawTotal = () => {
        return (
            <Card secondary style={{ "width": "auto", "padding": "5px 15px", "height": "94px", "backgroundColor": "var(--card-bg)", "borderRadius": "5px" }}>
                <Label style={{ "color": "var(--text)" }}>{<>{selected?.to_store_name}</>}</Label>
                <div style={{ "fontSize": "55px", "textAlign": "right", "fontWeight": "bold" }}>${drawPrice(results?.amount_owing, true)}</div>
            </Card>
        )
    }

    const DrawBtn = (props) => {
        const { print = false } = props;



        return (
            <div onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (print) {
                    finalizeOrder(printerRows);
                } else {
                    finalizeOrderWithoutReceipt();
                }
            }}>
                <Card className="card-btn" style={{ "width": "200px", "padding": "3px 15px" }}>
                    <div style={{ "fontSize": "28px", "textAlign": "center" }}>Finalize</div>
                    <Label style={{ "color": "var(--text)", "textAlign": "center", "marginTop": "-10px" }}><em><small>{(print) ? "Print / Send" : "without Printing"}</small></em></Label>

                    <div style={{ "textAlign": "center", "color": "var(--text-blue)" }}><strong>{(print) ? "PgDn" : "PgUp"}</strong></div>
                </Card>
            </div>
        )
    }


    const DrawFinalize = () => {

        const cols = [
            {
                min: true, width: 50, grow: true, key: "format", align: "left", title: "Format", render: (e, f) => {
                    return (<Space><Input cell width={30} value={"R"} style={{ "marginLeft": "-4px", "height": "25px" }} edit /> Receipt</Space>)
                }
            },
            {
                min: true, width: 50, grow: true, key: "destination", align: "left", title: "Destination", render: (e, f) => {
                    return (<Space><Input width={30} value={"E"} style={{ "marginLeft": "-4px", "height": "25px" }} cell edit /> Email</Space>)
                }
            },
            {
                min: true, width: 50, grow: true, key: "copies_options", align: "left", title: "Copies / Options", render: (e, f) => {
                    return (<Space><Input width={30} value={"2"} style={{ "marginLeft": "-4px", "height": "25px" }} cell edit /> Copies</Space>)
                }
            },
            {
                min: true, width: 190, grow: false, key: "printer_email_file", align: "left", title: "Printer / Email / File", render: () => {
                    return <Input width={187} style={{ "marginLeft": "-4px", "height": "25px" }} cell edit />
                }
            },
        ]

        const [rows, setRows] = useState([{
            format: "",
            destination: "",
            copies_options: "",
            printer_email_file: "",
        }])

        return (
            <div style={{ "padding": "0px", "paddingLeft": "0px", "height": "calc(100% - 20px)" }}>
                <Card header={<Label style={{ "textAlign": "left", "color": "var(--text)" }}>Finalize Transaction / Print / Send</Label>} style={{ "width": "100%", "height": "100%" }}>
                    <div style={{ "padding": "5px 10px" }}>
                        <Float>
                            <Space>
                                <Label>Sort by</Label>
                                <Input edit />
                                <Label>Make sort default?</Label>
                                <Checkbox />
                            </Space>
                            <Space>
                                <Label>B/O report?</Label>
                                <Input value="N" />
                                <Label><em>(no backorders found)</em></Label>
                            </Space>
                        </Float>
                        <div className="shim" />

                        <VirtualTable

                            controller={controller}
                            // onWheel={(e) => (e.deltaY > 0) ? next() : prev()}
                            // onRowClick={(item, event, index) => { setAlign("auto"); setSelected(item) }}
                            // next={nextRow}
                            // prev={prevRow}
                            align={align}
                            row_key={row_key}
                            current={selected}
                            data={rows}
                            height={200}
                            columns={cols}
                            width={0}
                            loading={[]}
                            offset={0}
                            route={route}
                            scrollbar={true}
                            scrollindicator={true}
                            infinite={true}
                        />
                    </div>
                </Card>
            </div>
        )
    }

    const { tabs, setTabs, index, help, update, setIndex, editProps, next, prev, refs, getVal } =

        useTabs(results.tenders.map(item => {
            return { ...item, "key": item.to_store_name, value: item.amount }
        }), "");

    const noteRefs = React.useRef({});

    const toggleRef = (tender) => {

        const update = tabs.map(tab => {

            if (tab.to_store_name !== tender.to_store_name) return tab;

            return {
                ...tab,
                to_store: !tab.to_store
            };

        });

        setTabs(update);
    };


    useEffect(() => {
        if (state.view !== "tender") return;

        const cleanup = listen(
            controller,
            route,
            tender_actions,
            false,
            [KBD.ARROW_DOWN, KBD.ARROW_UP, KBD.PAGE_DOWN, KBD.PAGE_UP, KBD.ESCAPE, KBD.MINUS]
        );

        return cleanup;
    }, [selected, tabs, state.view, loading]);


    useEffect(() => {

        const update = results.tenders.map(item => {
            return { ...item, "key": item.to_store_name, value: item.amount }
        })
        console.log("UPDATE HERE", update)
        setTabs(update)
    }, [results])



    useEffect(() => {
        setIndex(selected?.to_store_name)

        if (refs.current[selected?.to_store_name] && typeof refs.current[selected?.to_store_name].select === "function") {

            refs.current[selected?.to_store_name].select();
        }
    }, [selected])



    const openTenderNote = (name) => {
        setNotesOpen(prev => ({
            ...prev,
            [name]: true
        }));

        setTimeout(() => {
            noteRefs.current[name]?.focus?.();
        }, 0);
    };

    useEffect(() => {
        setNotesOpen(prev => {
            const next = { ...prev };

            tabs.forEach(tab => {
                const hasNote = (tab.note || "").trim() !== "";

                if (!hasNote) {
                    next[tab.to_store_name] = false;
                }
            });

            return next;
        });
    }, [index])


    const drawPrintOptions = () => {

        const print_cols = [
            { key: "format", title: <>Format</>, align: "left", width: 100, grow: true },
            { key: "destination", title: <>Destination</>, align: "right", width: 55, grow: true },
            { key: "copies", title: <>Copies / Options</>, align: "right", width: 55, grow: true },
            { key: "printer", title: <>Printer / Email / File</>, align: "right", width: 55, grow: true },
        ]

        return (
            <Card secondary
                header={<Float><div style={{ color: "var(--text)", "fontSize": "14px", "padding": "0px 5px" }}>Printing &amp; Sending Options</div><small style={{ "fontSize": "14px" }}><a><em>More Print Options&nbsp;&nbsp;</em></a></small></Float>}
                style={{ "borderRadius": "5px", "height": "100%" }}>
                <VirtualTable
                    controller={controller}

                    // headerClass="danger"
                    // onRowClick={(item, event, index) => { setAlign("auto"); setSelected(item) }}
                    // next={next}
                    // prev={prev}
                    // align={align}
                    // row_key={row_key}
                    // current={selected}
                    data={[]}
                    columns={print_cols}
                    // width={960}
                    // loading={false}
                    height={80}
                    offset={0}
                    route={route}
                />
            </Card>
        )
    }




    const drawProducts = () => {





        const cols = [
            { key: "title", title: <><Label className="light" style={{ "textAlign": "left" }}>&nbsp;Products</Label></>, align: "left", width: 100, grow: true },
            { key: "quantity", title: <>Qty</>, align: "right", width: 55 },
            {
                key: "price", title: <>Price</>, align: "right", width: 55, render: (e) => {
                    return drawPrice(e, true);
                }
            },
            { key: "discount", title: <>%</>, align: "right", width: 55 },
            { key: "tax_code", title: <>Tx</>, align: "right", width: 55 },
        ]

        return (
            <VirtualTable
                controller={controller}
                edit={false}
                // headerClass="danger"
                // onRowClick={(item, event, index) => { setAlign("auto"); setSelected(item) }}
                // next={next}
                // prev={prev}
                // align={align}
                // row_key={row_key}
                current={{}}
                data={products}
                scrollToEnd
                columns={cols}
                // width={960}
                // loading={false}
                height={224}
                offset={0}
                route={route}
            />
        )

    }



    const {
        amount = 0,
        sale_net = 0,
        calculated_totals = {},
    } = header || {};

    const {
        item_total = 0,
        sub_total = sale_net,
        tax_total = 0,
        taxes = [],
        total = amount,
        discounts = 0,
    } = calculated_totals || {};


    const pts = (header?.customer_ship_to?.frequent_buyer) ? header?.customer_ship_to?.frequent_buyer_point : "";


    const drawTotals = () => {
        return (
            <Card
                secondary
                style={{
                    width: "200px",
                    height: "224px",
                    borderRadius: "5px",
                    padding: 0,
                    boxSizing: "border-box",
                }}
            >
                <div
                    style={{
                        height: "205px",
                        display: "flex",
                        flexDirection: "column",
                        boxSizing: "border-box",
                    }}
                >
                    {/* HEADER */}
                    <div
                        style={{
                            color: "var(--text)",
                            fontSize: "14px",
                            padding: "0px 5px",
                            flex: "0 0 auto",
                        }}
                    >
                        Totals
                    </div>

                    {/* BODY */}
                    <div
                        style={{
                            flex: 1,
                            minHeight: 0,
                            padding: "5px",
                            paddingBottom: "1px",
                            display: "flex",
                            flexDirection: "column",
                            boxSizing: "border-box",
                        }}
                    >
                        {(header?.customer_ship_to?.ship_to &&
                            <div style={{ flex: "0 0 auto" }}>
                                <Float>
                                    <Label>Acct</Label>
                                    <Label>
                                        {header?.customer_ship_to?.ship_to}
                                    </Label>
                                </Float>
                            </div>
                        )}

                        {(pts &&
                            <div style={{ flex: "0 0 auto" }}>
                                <Float>
                                    <Label>FB</Label>
                                    <Label>
                                        {drawPrice(pts, true)}
                                    </Label>
                                </Float>
                            </div>
                        )}

                        {/* TOP */}
                        <div style={{ flex: "0 0 auto" }}>
                            <Float>
                                <Label>A/R</Label>
                                <Label style={{ color: (header?.customer_ar_total < 0) ? "var(--text-bright-green)" : "var(--text-red)" }}>
                                    {header?.customer_ar_total && <strong>{centsToPrice(Math.abs(header?.customer_ar_total))} {(header?.customer_ar_total < 0 && <>credit</>)}</strong>}
                                </Label>
                            </Float>
                        </div>


                        {/* SPACER */}
                        <div style={{ flex: "1 1 auto", minHeight: "10px" }} />

                        {/* BOTTOM */}
                        <div style={{ flex: "0 0 auto" }}>
                            <Float>
                                <span>&nbsp;</span>
                                <Label>
                                    {(productsTotal && <em>{productsTotal} sold</em>)}
                                </Label>
                            </Float>

                            <Divider style={{ marginTop: "-3px" }} />

                            <Float>
                                <Label>Subtotal</Label>
                                <Label style={{ color: "var(--text)" }}>
                                    {drawPrice(sub_total, true)}
                                </Label>
                            </Float>

                            {(discounts !== 0) && (
                                <>
                                    <div className="shim" style={{ height: "2px" }} />
                                    <Float>
                                        <Label>Discounts</Label>
                                        <Label style={{ color: "var(--text)" }}>
                                            {drawPrice(discounts, true)}
                                        </Label>
                                    </Float>
                                </>
                            )}

                            {taxes.map((tax, index) => {
                                const {
                                    amount = 0,
                                    code = "",
                                    name = "",
                                } = tax || {};

                                return (
                                    <React.Fragment key={`${code}-${name}-${index}`}>
                                        <div className="shim" style={{ height: "2px" }} />
                                        <Float>
                                            <Label>
                                                {code} {name}
                                            </Label>
                                            <Label style={{ color: "var(--text)" }}>
                                                {drawPrice(amount, true)}
                                            </Label>
                                        </Float>
                                    </React.Fragment>
                                );
                            })}

                            <Divider />

                            <div className="shim" />

                            <Input
                                medium
                                style={{ backgroundColor: "var(--text-red)" }}
                                width={165}
                                value={
                                    <Float>
                                        <small>Total</small>
                                        <div>{drawPrice(total, true)}</div>
                                    </Float>
                                }
                            />
                        </div>
                    </div>
                </div>
            </Card>
        )
    }

    const commitSelectedPrice = (move = 0) => {
        const key = selected?.to_store_name;
        if (!key) return;
        const value = getVal(key);
        const tab = tabs.find(item => item.to_store_name === key) || selected;


        if (!tab?.to_store && !value) {
            toggleRef(selected)
        }

        if (tab?.amount === value) {
            // don't update, just move
            if (move === 1) {
                nextRow()
            }
            if (move === -1) {
                prevRow();
            }
            return;
        }

        const updatedTender = {
            ...tab, amount: parseInt(value || 0)
        };

        update(key, value);

        setTenderOptions(value, updatedTender, false, move);
    };

    const drawTendering = () => {

        const columns = [
            {
                width: 400,
                key: "tender",
                border: false,
                align: "left",
                title: <Float>
                    <span style={{ color: "var(--text)" }}>Choose a tender</span>
                    <div style={{ color: "var(--text-yellow)", maxWidth: "250px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", }} >
                        <small>{getCashNote(header?.notes)}</small>
                    </div>
                </Float>,
                render: (e, f) => {
                    const rawAmount = Number(getVal(f.amount) || state.prefill || 0);

                    const cashAdjustment = isCashTender(f)
                        ? getCashAdjustment(rawAmount)
                        : 0;

                    const cashPrefill = rawAmount + cashAdjustment;
                    const fxValues = getForeignCurrencyValues(results?.amount_owing || 0);

                    const getPricePrefill = () => {
                        if (isDiscountTender(f)) return 0;
                        if (isForeignCurrencyTender(f)) return fxValues.foreignAmount;
                        if (isCashTender(f)) return cashPrefill;

                        return state.prefill;
                    };

                    const pricePrefill = getPricePrefill();

                    const showExchangeLabel =
                        isExchangeTender(f) && isForeignCurrencyTender(selected);

                    const showPennyAdjustment =
                        isCashTender(f) &&
                        selected?.amount === f?.amount &&
                        cashAdjustment !== 0;

                    const hasTenderNote = (f.note || "").trim() !== "";
                    const showTenderNoteInput = notesOpen[f.to_store_name] || hasTenderNote;

                    return (

                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 4,
                                padding: "0"
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "stretch",
                                    gap: 0,
                                    width: "100%",
                                    minHeight: 25
                                }}
                            >

                                <div
                                    style={{
                                        width: 150,
                                        minWidth: 150,
                                        maxWidth: 150,
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        textOverflow: "ellipsis"
                                    }}
                                >
                                    <Space>
                                        <span style={{ "color": (f?.to_store) ? "var(--text)" : "var(--text-teal)" }}>{(f?.to_store) ? f.to_store_name : f?.to_customer_name}</span>
                                    </Space>
                                </div>

                                {showExchangeLabel ? (
                                    <div
                                        style={{
                                            width: 100,
                                            minWidth: 100,
                                            maxWidth: 100
                                        }}
                                    >
                                        <Label style={{ color: "var(--text)", "paddingRight": "6px" }}>
                                            <em><small>{fxValues.label}</small></em>
                                        </Label>
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            width: 100,
                                            minWidth: 100,
                                            maxWidth: 100
                                        }}
                                    >

                                        <PriceField
                                            hide_zero={true}
                                            prefill={pricePrefill}
                                            price
                                            cell
                                            width={90}
                                            right
                                            style={{
                                                height: "25px",
                                                textAlign: "left",
                                                padding: "0px 5px"
                                            }}
                                            {...editProps(f?.to_store_name)}
                                            edit={state?.view !== "print" && selected?.to_store_name === f?.to_store_name}

                                            {...(
                                                state?.view === "print" && selected?.to_store_name === f?.to_store_name
                                                    ? { value: pricePrefill }
                                                    : {}
                                            )}



                                            onChange={(e) => { update(f?.to_store_name, e); }}
                                            onEnter={(e) => {
                                                update(f?.to_store_name, e);

                                                if (isCashTender(f)) {
                                                    const amount = parseInt(e || 0);

                                                    if (amount > results.amount_owing) {
                                                        setTenderOptions(amount, { ...f, amount });
                                                    }


                                                    return;
                                                }

                                                openTenderNote(f.to_store_name);
                                            }}
                                            onTab={(e) => {
                                                update(f?.to_store_name, e);
                                                openTenderNote(f?.to_store_name);
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Notes / messages - uses remaining space */}
                                <div
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        //backgroundColor: "var(--text-black)",
                                        minHeight: 25,
                                        padding: "0 6px",
                                        margin: 0,
                                        alignSelf: "stretch",
                                        marginRight: "-5px"
                                    }}
                                >
                                    {showPennyAdjustment && (selected?.to_store_name === f?.to_store_name) && (
                                        <Label
                                            style={{
                                                color: "var(--text)",
                                                marginTop: "2px",
                                                textAlign: "left",
                                                fontSize: "14px",
                                                fontWeight: "normal"
                                            }}
                                        >
                                            <small>
                                                <em>
                                                    {cashAdjustment > 0 ? "+" : ""}
                                                    {cashAdjustment}¢ Penny Adj
                                                </em>
                                            </small>
                                        </Label>
                                    )}


                                    {!showTenderNoteInput && state?.view !== "print" && selected?.to_store_name === f?.to_store_name && !isCashTender(f) && (
                                        <a
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                openTenderNote(f.to_store_name);
                                            }}
                                            style={{
                                                cursor: "pointer",
                                                fontSize: "16px",
                                                fontWeight: "normal",
                                                textDecoration: "none",

                                            }}
                                        >
                                            <em>
                                                <small>Add tender note</small>
                                            </em>
                                        </a>
                                    )}

                                    {notesOpen[f.to_store_name] && (
                                        <Input
                                            ref={(el) => {
                                                noteRefs.current[f.to_store_name] = el;
                                            }}
                                            width="100%"
                                            cell
                                            edit={selected?.to_store_name === f?.to_store_name}
                                            value={f.note || ""}
                                            onChange={(val) => {
                                                const updatedTabs = tabs.map(tab =>
                                                    tab.to_store_name === f.to_store_name
                                                        ? { ...tab, note: val }
                                                        : tab
                                                );

                                                setTabs(updatedTabs);
                                            }}
                                            style={{
                                                height: "25px",
                                                textAlign: "left",
                                                padding: "0px 5px",
                                                width: "100%",

                                            }}



                                            onEnter={(val) => {
                                                const tab =
                                                    tabs.find(item => item.to_store_name === f.to_store_name) || f;

                                                setTenderOptions(
                                                    getVal(f.to_store_name),
                                                    {
                                                        ...tab,
                                                        note: val
                                                    }
                                                );
                                                // back to input field..
                                                setTimeout(() => {
                                                    refs.current[f.to_store_name]?.focus?.();
                                                    refs.current[f.to_store_name]?.select?.();
                                                }, 0);
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div >

                    );
                }
            }
        ];



        return (
            <div className={(state.view === "print" || state.view === "print_edit") ? "dimmed" : ""} style={{ "margin": "10px", "width": "400px" }}>

                <VirtualTable
                    no_header_borders={true}
                    controller={controller}

                    // onWheel={(e) => (e.deltaY > 0) ? next() : prev()}
                    onRowClick={(item, event, index) => {
                        if (state?.view === "print" || state.view === "print_edit") {
                            return;
                        }
                        if (!isSelectableTender(item)) return;
                        setAlign("auto");
                        setSelected(item)
                    }}
                    next={next}
                    prev={prev}

                    align={"bottom"}
                    row_key={row_key}
                    current={selected}
                    data={tabs || []}
                    height={500}
                    columns={columns}
                    width={0}
                    // loading={loading}
                    offset={148}
                    route={route}
                    scrollbar={true}
                    scrollindicator={true}
                    infinite={true}
                />

                {/* <div style={{ "padding": "3px 3px" }}>
                    <Space size={20}>
                        <Space><a><strong style={{ "fontWeight": "800", "fontSize": "16px" }}>PgDn</strong></a><Label>Complete</Label></Space>
                        <Space><a><strong style={{ "fontWeight": "800", "fontSize": "16px" }}>PgUp</strong></a><Label>Complete <em>(no receipt)</em></Label></Space>
                    </Space>

                </div> */}

            </div>
        )
    }


    return (
        <Spin loading={loading}>
            <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 92px)", minHeight: "557px", width: "100%", "backgroundColor": "var(--console-bg)", "borderRadius": "10px" }}>

                <div style={{ display: "flex", flex: 1, minHeight: 0, "gap": "0px" }}>


                    <div style={{ display: "flex", alignItems: "flex-start", width: "410px" }}>
                        {drawTendering()}
                    </div>



                    <div style={{ display: "flex", flexDirection: "column", flex: "1 1 0%", minWidth: 0, minHeight: 0, width: "100%", height: "100%", gap: 12, padding: "10px", "paddingBottom": "0px", boxSizing: "border-box" }} >

                        <div style={{ display: "flex", width: "100%", alignItems: "stretch", gap: 20 }}>

                            <div style={{ minWidth: 0, display: "flex" }}>
                                <div style={{ height: "222px", display: "flex" }}>
                                    {drawTotals()}
                                </div>
                            </div>


                            <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 8, }} >
                                <div style={{ flex: "1 1 0%" }}>
                                    <Button style={{ "width": "240px" }} className="ui-card-btn">POS Defaults</Button>
                                </div>
                                <div style={{ flex: "1 1 0%" }}>
                                    <Button style={{ "width": "240px" }} className="ui-card-btn">Invoice & Receipt Set-up</Button>
                                </div>
                                <div style={{ flex: "1 1 0%" }}>
                                    <Button style={{ "width": "240px" }} className="ui-card-btn">View Online Order</Button>
                                </div>
                                <div style={{ flex: "1 1 0%" }}>
                                    <Button onClick={() => {
                                        callback("exit")

                                    }} style={{ "width": "240px" }} className="ui-card-btn">Hold Sale</Button>
                                </div>
                            </div>


                            <div style={{ flex: "1 1 0%", minWidth: 0, display: "flex" }} >
                                <div style={{ flex: 1 }}>
                                    {drawProducts()}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: "flex", width: "100%", flex: "1 1 0%", minHeight: 0, }} >
                            <div style={{ flex: 1, minHeight: 0 }}>
                                <PrintOptions
                                    header={header}
                                    rows={printerRows}
                                    setRows={setPrinterRows}
                                    startInEdit={false}
                                    controller={controller}
                                    route={ROUTES.POS.PRINT}
                                    editRoute={ROUTES.POS.PRINT_EDIT}
                                    active={isPrintView}
                                    onBack={switchToTenders}
                                    onComplete={finalizeOrder}
                                />
                            </div>
                        </div>



                        <div style={{ display: "flex", width: "100%", gap: 8, flex: "0 0 auto" }} >
                            <div style={{ flex: "1 1 0%", minWidth: 0 }}>
                                <Card secondary style={{ "padding": "0px", "marginRight": "8px", "backgroundColor": "var(--card-bg)", "borderRadius": "5px" }}>
                                    <Space size={10}>
                                        <DrawBtn print={true} />
                                        <DrawBtn print={false} />
                                    </Space>
                                </Card>
                            </div>

                            <div style={{ flex: "1 1 0%", minWidth: 0 }}>
                                <DrawTotal />
                            </div>
                        </div>
                    </div>
                </div>


                <div className="shim" />
                <Divider />
                {/* FULL WIDTH BOTTOM ROW */}
                <div style={{ height: "30px", width: "100%", alignItems: "center", padding: "0 12px", boxSizing: "border-box", "padding": "2px 10px" }}>
                    <Status type="active"><InfoIcon />{(notesOpen[selected.to_store_name]) ? "Attach a note to this tender" : "Complete sale [PgDn] or [PgUp] (no receipt)"}</Status>
                </div>

            </div>

            {/* Pinpad payment in progress - not interactable */}
            {(captureAmount !== null) && (
                <div onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }} onClick={(e) => e.stopPropagation()} style={{ "position": "fixed", "inset": 0, "zIndex": 2000, "backgroundColor": "rgba(0,0,0,0.4)", "display": "flex", "alignItems": "center", "justifyContent": "center", "cursor": "wait" }}>
                    <Card style={{ "width": "320px", "padding": "20px", "textAlign": "center", "lineHeight": "20px" }}>
                        <div style={{ "margin": "0 auto", "height": "20px", "width": "20px" }} className="loader" />
                        <div className="shim" /><div className="shim" />
                        <strong>Card payment in progress...</strong>
                        <div className="shim" /><div className="shim" />
                        Amount: <strong>${drawPrice(captureAmount, true)}</strong>
                        <div className="shim" /><div className="shim" />
                        <span style={{ "color": "var(--text-dim)" }}>Waiting for payment service response</span>
                    </Card>
                </div>
            )}
        </Spin>
    )

}
